import { useEffect, useState, type FormEvent } from 'react';
import { copyText } from '../../lib/clipboard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Check, Copy, Link2, Plus } from 'lucide-react';
import type { LibraryFile, ShareLink } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate, isValidEmail, relativeTime, shortDate } from '../../lib/format';
import { Badge, Button, Drawer, EmptyState, ErrorState, Field, Input, Notice, SkeletonLines, useConfirm, useToast } from '../ui';

function linkState(l: ShareLink): { label: string; tone: 'success' | 'danger' | 'neutral' } {
  if (l.revokedAt) return { label: 'Revoked', tone: 'danger' };
  if (new Date(l.expiresAt).getTime() <= Date.now()) return { label: 'Expired', tone: 'neutral' };
  return { label: 'Active', tone: 'success' };
}

export function ShareLinksDrawer({ file, open, onClose }: { file: LibraryFile | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const id = file?.id ?? '';

  const links = useQuery({ queryKey: qk.libraryLinks(id), queryFn: () => api('GET /api/library/:id/links', { params: { id } }), enabled: open && !!id });

  const [recipient, setRecipient] = useState('');
  const [days, setDays] = useState('7');
  const [created, setCreated] = useState<ShareLink | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setCreated(null);
      setCopied(false);
      setRecipient('');
      setDays('7');
    }
  }, [open]);

  const daysNum = Number(days);
  const daysValid = Number.isInteger(daysNum) && daysNum >= 1 && daysNum <= 365;
  const recipientValid = !recipient.trim() || isValidEmail(recipient.trim());

  const create = useMutation({
    mutationFn: () =>
      api('POST /api/library/:id/links', {
        params: { id },
        body: { recipientEmail: recipient.trim() || undefined, expiresInDays: daysNum },
      }),
    onSuccess: (r) => {
      setCreated(r.link);
      setCopied(false);
      setRecipient('');
      void qc.invalidateQueries({ queryKey: qk.libraryLinks(id) });
    },
    onError: (e) => toast.error(e),
  });

  const revoke = useMutation({
    mutationFn: (linkId: string) => api('POST /api/share-links/:id/revoke', { params: { id: linkId } }),
    onSuccess: () => {
      toast.success('Link revoked — it no longer works.');
      void qc.invalidateQueries({ queryKey: qk.libraryLinks(id) });
    },
    onError: (e) => toast.error(e),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!daysValid || !recipientValid) return;
    create.mutate();
  };

  const copy = async (url: string) => {
    try {
      await copyText(url);
      setCopied(true);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy automatically — select the link and copy it manually.');
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={file ? `Share links — ${file.friendlyName}` : 'Share links'} width={460}>
      <div className="space-y-5 p-4">
        <p className="text-[0.8125rem] text-muted">
          Protected links expire, can be revoked at any time, and record every access. There are no permanent public file URLs.
        </p>

        {file?.supersededById && (
          <Notice tone="warn" icon={<AlertTriangle className="h-4 w-4" />}>
            This version has been replaced. New links should be created from the newest version.
          </Notice>
        )}

        <form onSubmit={onSubmit} className="space-y-3 rounded-2xl border border-hairline bg-raised/50 p-4" aria-label="Create a share link">
          <h3 className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
            <Plus className="h-3.5 w-3.5" aria-hidden /> Create link
          </h3>
          <Field label="Recipient email (optional)" hint="Restricts the link to one recipient when set." error={recipientValid ? undefined : 'Enter a valid email address.'}>
            {(fid) => <Input id={fid} type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="name@example.com" className="max-md:h-11" />}
          </Field>
          <Field label="Expires in (days)" error={daysValid ? undefined : 'Choose between 1 and 365 days.'}>
            {(fid) => <Input id={fid} type="number" min={1} max={365} step={1} value={days} onChange={(e) => setDays(e.target.value)} className="max-md:h-11" />}
          </Field>
          <Button type="submit" variant="primary" icon={<Plus className="h-4 w-4" />} loading={create.isPending} disabled={!daysValid || !recipientValid || !!file?.supersededById} className="max-md:h-11">
            Create link
          </Button>
        </form>

        {created?.url && (
          <div className="space-y-2 rounded-2xl border border-warn/40 bg-warn-soft p-4" role="status">
            <p className="flex items-center gap-2 text-[0.8125rem] font-semibold text-warn-ink">
              <AlertTriangle className="h-4 w-4" aria-hidden /> This URL is shown only once
            </p>
            <p className="text-xs text-warn-ink">Copy it now. For security it cannot be displayed again — create a new link if you lose it.</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={created.url} aria-label="New share link URL" onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <Button variant="primary" icon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} onClick={() => void copy(created.url ?? '')} className="max-md:h-11">
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-warn-ink">
              {created.recipientEmail ? `For ${created.recipientEmail} · ` : ''}Expires {fullDate(created.expiresAt)}
            </p>
          </div>
        )}

        <section aria-label="Existing links" className="space-y-2">
          <h3 className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
            <Link2 className="h-3.5 w-3.5" aria-hidden /> Existing links
            {!!links.data?.links.length && <span className="ml-auto rounded-full bg-raised px-1.5 text-[0.625rem] font-semibold leading-4 text-muted">{links.data.links.length}</span>}
          </h3>
          {links.isLoading ? (
            <SkeletonLines lines={4} />
          ) : links.isError ? (
            <ErrorState compact error={links.error} onRetry={() => void links.refetch()} />
          ) : !links.data?.links.length ? (
            <EmptyState icon={<Link2 className="h-6 w-6" />} title="No links yet" description="Create a link above to share this file securely." className="py-6" />
          ) : (
            <ul className="space-y-2">
              {links.data.links.map((l) => {
                const s = linkState(l);
                return (
                  <li key={l.id} className="rounded-xl border border-hairline bg-surface p-3 transition-colors hover:bg-raised/60">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[0.8125rem] font-medium text-ink">{l.recipientEmail ?? 'Any recipient with the link'}</p>
                        <p className="text-xs text-muted">
                          Created {shortDate(l.createdAt)} · {l.revokedAt ? `revoked ${relativeTime(l.revokedAt)}` : `expires ${fullDate(l.expiresAt)}`}
                        </p>
                        <p className="text-xs text-muted">
                          {l.accessCount} {l.accessCount === 1 ? 'access' : 'accesses'}
                          {l.lastAccessAt ? ` · last ${relativeTime(l.lastAccessAt)}` : ' · never opened'}
                        </p>
                      </div>
                      <Badge tone={s.tone}>{s.label}</Badge>
                    </div>
                    {s.label === 'Active' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2 text-danger-ink max-md:h-11"
                        icon={<Ban className="h-4 w-4" />}
                        loading={revoke.isPending && revoke.variables === l.id}
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Revoke this link?',
                            message: 'Anyone using it will no longer be able to download the file. This cannot be undone.',
                            confirmLabel: 'Revoke',
                            danger: true,
                          });
                          if (ok) revoke.mutate(l.id);
                        }}
                      >
                        Revoke
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Drawer>
  );
}
